/**
 * `stream.*` events for the telemetry `net` group: one tracer per
 * SSE stream records `stream.start`, the frames — consecutive frames of one type
 * coalesced into one `stream.frame` per second with their count, bytes and first /
 * last `t_mono` — and how the stream ended (`stream.end` / `stream.error` /
 * `stream.abort`). Each record goes through `recordTraceEvent`, so nothing is kept
 * while the group is off.
 */
import { recordTraceEvent, telemetryGroupOn } from "./groups.js";
import { scrubFreeText } from "./sanitize.js";

/** Consecutive same-type frames within this window share one `stream.frame`. */
export const FRAME_WINDOW_MS = 1000;

/** Responses the net capture saw, to the `x-client-req-id` it sent (services/netCapture.js). */
const reqIds = new WeakMap();

/** Note the `x-client-req-id` a response was requested with. */
export function noteClientReqId(response, id) {
  if (response && typeof response === "object" && id) reqIds.set(response, id);
}

/** The `x-client-req-id` sent for `response`, or null. */
export function clientReqIdOf(response) {
  return (response && typeof response === "object" && reqIds.get(response)) || null;
}

/**
 * Yield `frames` through, closing `trace` by how the stream ended: `stream.end` on a
 * clean finish, `stream.abort` on an abort (or a consumer that stopped early),
 * `stream.error` on anything else. `onTerminal(outcome)`, when given, hears the run's
 * outcome from its terminal frame (`ok` / `error` / `cancelled`; the intent emitter ends
 * the run's intent with it, P2 design §4.6).
 */
export async function* traceStream(trace, signal, frames, { onTerminal = null } = {}) {
  let finished = false;
  try {
    for await (const item of frames) {
      // A terminal frame settles the stream before the consumer sees it: consumers stop
      // iterating right after one, which must not read as an early close.
      const outcome = settleOnTerminal(trace, item);
      if (outcome && onTerminal) {
        try {
          onTerminal(outcome);
        } catch {
          /* telemetry never breaks the stream */
        }
      }
      yield item;
    }
    finished = true;
    // EOF without a terminal frame is a cut (the proxy ends the response after an
    // upstream failure), not a clean end; a terminal frame has already settled it.
    if (signal?.aborted) trace.abort();
    else trace.error(new Error("stream ended without a terminal frame"));
  } catch (e) {
    if (e?.name === "AbortError" || signal?.aborted) trace.abort();
    else trace.error(e);
    throw e;
  } finally {
    if (signal?.aborted) trace.abort();
    else if (finished) trace.end();
    else trace.abort("closed");
  }
}

/**
 * Close `trace` by a terminal frame (`{type, data}`): done, error or cancelled. Returns the
 * run's outcome for a terminal frame (`ok` / `error` / `cancelled`), else null.
 */
function settleOnTerminal(trace, item) {
  const type = item?.type;
  // The parser hands a done frame with unparseable JSON on as `data: null`: corrupt,
  // not a clean completion.
  if (type === "done") {
    // A done without a result URL (unparseable JSON arrives as `data: null`) is a
    // failed run that recovery rejects, not a clean completion.
    const url = item?.data?.result_url;
    if (typeof url === "string" && url.length > 0) {
      trace.end();
      return "ok";
    }
    trace.error(new Error("invalid done frame"));
    return "error";
  }
  if (type === "error") {
    const d = item?.data || {};
    trace.error(new Error(d.message || d.code || d.error || "error frame"));
    return "error";
  }
  if (type === "cancelled") {
    trace.abort("cancelled");
    return "cancelled";
  }
  return null;
}

const monoNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

const encoder = new TextEncoder();

/** UTF-8 length of `text` (frame sizes are bytes, not UTF-16 code units). */
export function utf8Length(text) {
  return typeof text === "string" ? encoder.encode(text).length : 0;
}

/**
 * A tracer for one stream. `ids` are the STREAM fields (`stream`, `trace_id`,
 * `forecast_id`, `run_id`, `intent_id`); a no-op while the net group is off.
 */
export function createStreamTrace(
  ids,
  {
    record = recordTraceEvent,
    now = monoNow,
    enabled = () => telemetryGroupOn("net"),
    setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
    clearTimeoutImpl = (id) => clearTimeout(id),
  } = {},
) {
  // Never fixed at creation: the group can turn on mid-stream (an /api/me refresh), and
  // a tracer created while it was off then starts recording, opening with stream.start.
  const base = { ...ids };
  let startExtra = null;
  let started = false;
  const t0 = now();
  let frames = 0;
  let bytes = 0;
  let run = null;
  /** Trailing flush of the open run, so a lone frame on an idle stream is not held. */
  let runTimer = null;
  let done = false;

  function emitStart() {
    if (started || startExtra === null) return;
    started = true;
    emit({ event: "stream.start", ...pick(startExtra, ["route", "request_id", "client_req_id"]) });
  }

  function emit(event) {
    if (!enabled()) return;
    try {
      record({ ...base, ...event });
    } catch {
      /* never surface */
    }
  }

  function flushRun() {
    if (runTimer !== null) clearTimeoutImpl(runTimer);
    runTimer = null;
    if (!run) return;
    emit({
      event: "stream.frame",
      type: run.type,
      count: run.count,
      bytes: run.bytes,
      first_t_mono: run.first,
      last_t_mono: run.last,
    });
    run = null;
  }

  function finish(event) {
    if (done) return;
    done = true;
    if (enabled()) emitStart();
    flushRun();
    emit({ ...event, duration_ms: Math.round(now() - t0) });
  }

  return {
    /** Whether events are recorded now (follows the group); per-frame work checks it. */
    get active() {
      return enabled();
    },
    /** The stream opened; `extra` carries `route`, `request_id`, `client_req_id`. */
    start(extra = {}) {
      Object.assign(base, pick(extra, ["trace_id", "forecast_id", "run_id"]));
      startExtra = extra;
      if (enabled()) emitStart();
    },
    /** One frame given as its text, sized only while tracing (`overhead`: delimiter bytes). */
    frameOf(type, text, overhead = 0) {
      if (done || !enabled()) return;
      this.frame(type, utf8Length(text) + overhead);
    },
    /** One frame of `type` and `size` UTF-8 bytes. */
    frame(type, size = 0) {
      if (done || !enabled()) return;
      emitStart();
      const t = now();
      frames += 1;
      bytes += size;
      if (run && (run.type !== type || t - run.first >= FRAME_WINDOW_MS)) flushRun();
      if (!run) {
        run = { type, count: 0, bytes: 0, first: t, last: t };
        runTimer = setTimeoutImpl(flushRun, FRAME_WINDOW_MS);
      }
      run.count += 1;
      run.bytes += size;
      run.last = t;
    },
    end() {
      finish({ event: "stream.end", frames, bytes });
    },
    error(err) {
      const status = /^HTTP (\d{3})$/.exec(err?.message || "");
      finish({
        event: "stream.error",
        // Backend text (a row's error_message) may carry emails, URLs or secrets.
        message: scrubFreeText(err?.message || err || "error", 300),
        status: status ? Number(status[1]) : null,
      });
    },
    abort(reason = "aborted") {
      finish({ event: "stream.abort", reason });
    },
  };
}

function pick(obj, keys) {
  const out = {};
  for (const k of keys) if (obj[k] !== undefined) out[k] = obj[k];
  return out;
}
